import { ChangeDetectorRef, Component, Input, Optional, Self, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';
import { DeviceType } from '@app/core/types/common/device.types';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { Capacitor } from '@capacitor/core';
import { faCalendar, faClock, faXmark } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { locale, MbscModule } from '@mobiscroll/angular';
import { DateTime } from 'luxon';

@Component({
  selector: 'c-date-picker',
  imports: [SharedModule, TranslocoPipe, MbscModule, ButtonComponent],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss'
})
export class DatePickerComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translationSandbox = inject(TranslationSandbox);

  @Input() label: string;
  @Input() placeholder: string;
  @Input() minDate: string;
  @Input() maxDate = '2099-12-31';
  @Input() showError: boolean;
  @Input() showClearButton: boolean = false;

  faCalendar = faCalendar;
  faClock = faClock;
  faXmark = faXmark;
  isPickerOpen = false;

  readonly localeAll = locale;
  readonly currentLocale = this.translationSandbox.currentLocale;

  datePickerValue: Date;

  onChange = () => {};
  onTouched = () => {};

  get control() {
    return this.ngControl?.control as FormControl;
  }

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  setPickerOpen(open: boolean): void {
    this.isPickerOpen = open;
    this.cdr.detectChanges();
  }

  willPresentHandler(): void {
    if (Capacitor.getPlatform() === DeviceType.IOS) {
      this.setDefaultValue(); 
    }
  }

  didPresentHandler(): void {
    if (Capacitor.getPlatform() === DeviceType.Android) {
      this.setDefaultValue();
    }
  }

  writeValue(): void {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDefaultValue(): void {
    this.datePickerValue = this.control.value;
  }

  cancelDatePicker(): void {
    this.clearSelectedDate();
    this.setPickerOpen(false);
  }

  clearControlValue(): void {
    this.control.setValue(null);
  }

  clearSelectedDate(): void {
    this.setDefaultValue();
  }

  confirmSelectedDate(): void {
    this.control.setValue(
      DateTime.fromJSDate(this.datePickerValue).toISODate()
    );
    this.setPickerOpen(false);
  }
}
